import type { ReactNode } from 'react';

import {
  CustomButtonPart,
  CustomHeadingPart,
  CustomImagePart,
  CustomTextPart,
  DividerPart,
  SpacerPart,
  type CommonPartProps,
} from '@/features/storefront/parts/CommonParts';
import {
  ProductAccordionPart,
  ProductBreadcrumbsPart,
  ProductComparisonPart,
  ProductHighlightsPart,
  ProductPaymentPart,
  ProductSharePart,
} from '@/features/storefront/parts/ProductExtraParts';
import {
  ProductAvailabilityPart,
  ProductBrandPart,
  ProductBuyNowPart,
  ProductComparePricePart,
  ProductDescriptionPart,
  ProductGalleryPart,
  ProductPricePart,
  ProductQuantityPart,
  ProductRecommendationsPart,
  ProductRelatedPart,
  ProductShippingInfoPart,
  ProductShortDescriptionPart,
  ProductSkuPart,
  ProductSpecsPart,
  ProductTaxNotePart,
  ProductTitlePart,
  ProductVariantsPart,
} from '@/features/storefront/parts/ProductParts';
import { ProductBuyOnWhatsAppPart } from '@/features/storefront/parts/WhatsAppParts';
import {
  ProductDeliveryEstimatePart,
  ProductOffersPart,
  ProductSizeChartPart,
  ProductStickyCartPart,
  ProductStockBarPart,
} from '@/features/storefront/parts/ProductCommerceParts';
import { ProductBundlePart } from '@/features/storefront/parts/ProductBundlePart';
import {
  ProductNotifyMePart,
  ProductQaPart,
} from '@/features/storefront/parts/ProductEngagementParts';
import { ProductSellerInfoPart } from '@/features/storefront/parts/SellerInfoPart';
import type { PartType } from '@/features/storefront/sections/parts';
import { ProductAddToCartWithWishlistPart } from '@/features/storefront/wishlist/ProductWishlistPart';

/**
 * Every component the storefront can draw for itself, by the type that names it.
 *
 * The other half of the answer is a page's `slots` - a finished node handed over
 * by the page that owns the logic, which is right for a checkout form or a
 * catalog grid and wrong for a product's title. `PartRegion` asks the slots
 * first and this second; a type in neither renders nothing, which is what a
 * component belonging to a page you are not on should do.
 *
 * In its own module so the component files export components and nothing else,
 * which is what keeps hot reload working while somebody is editing one of them.
 */
export const PART_COMPONENTS: Partial<
  Record<PartType, (props: CommonPartProps) => ReactNode>
> = {
  // The merchant's own furniture, welcome on any page.
  custom_heading: CustomHeadingPart,
  custom_text: CustomTextPart,
  custom_button: CustomButtonPart,
  custom_image: CustomImagePart,
  divider: DividerPart,
  spacer: SpacerPart,

  // The product page, which is the one taken all the way apart.
  product_breadcrumbs: ProductBreadcrumbsPart,
  product_gallery: ProductGalleryPart,
  product_brand: ProductBrandPart,
  product_title: ProductTitlePart,
  product_price: ProductPricePart,
  product_compare_price: ProductComparePricePart,
  product_tax_note: ProductTaxNotePart,
  product_sku: ProductSkuPart,
  product_availability: ProductAvailabilityPart,
  product_short_description: ProductShortDescriptionPart,
  product_highlights: ProductHighlightsPart,
  product_variants: ProductVariantsPart,
  product_quantity: ProductQuantityPart,
  // The plain button, plus the wishlist heart beside it while the plugin asks.
  product_add_to_cart: ProductAddToCartWithWishlistPart,
  product_buy_now: ProductBuyNowPart,
  product_buy_on_whatsapp: ProductBuyOnWhatsAppPart,
  product_description: ProductDescriptionPart,
  product_specs: ProductSpecsPart,
  product_shipping_info: ProductShippingInfoPart,
  product_payment: ProductPaymentPart,
  product_share: ProductSharePart,
  product_related: ProductRelatedPart,
  product_recommendations: ProductRecommendationsPart,
  product_comparison: ProductComparisonPart,
  product_accordion: ProductAccordionPart,
  product_delivery_estimate: ProductDeliveryEstimatePart,
  product_stock_bar: ProductStockBarPart,
  product_offers: ProductOffersPart,
  product_sticky_cart: ProductStickyCartPart,
  product_size_chart: ProductSizeChartPart,
  product_bundle: ProductBundlePart,
  product_qa: ProductQaPart,
  product_notify_me: ProductNotifyMePart,
  product_seller_info: ProductSellerInfoPart,
};
